<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="staff-modal"
    :title="edited.id ? 'Edit staff member' : 'Add staff member'"
    ok-title="Save"
    cancel-variant="outline-secondary"
    :ok-disabled="isFormLoading"
    @ok.prevent="onSubmit"
  >
    <div
      v-if="isContentLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Name"
            label-for="staff-name"
          >
            <b-form-input
              id="staff-name"
              v-model="edited.name"
              placeholder="Name"
            />
            <small
              v-if="v$.edited.name.$error"
              class="text-danger"
            >Name is required</small>
          </b-form-group>

          <b-form-group
            label="Picture"
            label-for="staff-picture"
          >
            <b-form-file
              id="staff-picture"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              @change="onPictureChange"
            />
            <b-img
              v-if="picturePreview"
              thumbnail
              rounded="circle"
              fluid
              :src="picturePreview"
              class="mt-1"
              :style="{ width: '120px', height: '120px', objectFit: 'cover' }"
              alt=""
            />
          </b-form-group>

          <b-form-group
            label="Email"
            label-for="staff-email"
          >
            <b-form-input
              id="staff-email"
              v-model="edited.email"
              type="email"
              placeholder="Email"
            />
            <small
              v-if="v$.edited.email.$error"
              class="text-danger"
            >Valid email is required</small>
            <small
              v-else-if="emailError"
              class="text-danger"
            >{{ emailError }}</small>
          </b-form-group>

          <b-form-group
            label="Phone number"
            label-for="staff-phone"
            class="mb-2"
          >
            <PhoneNumberInput
              id="staff-phone"
              v-model:dial="staffDial"
              v-model:number="staffNumber"
              required
              @change="onPhoneInput"
            />
            <small
              v-if="phoneError"
              class="text-danger"
            >{{ phoneError }}</small>
          </b-form-group>

          <b-form-group
            label="Description"
            label-for="staff-description"
          >
            <b-form-textarea
              id="staff-description"
              v-model="edited.description"
              placeholder="Description"
              rows="3"
              max-rows="6"
            />
          </b-form-group>

          <b-form-group
            v-if="!edited.id"
            label="Password"
            label-for="staff-password"
          >
            <b-form-input
              id="staff-password"
              v-model="edited.password"
              type="text"
              placeholder="Password"
            />
            <small
              v-if="v$.edited.password.$error"
              class="text-danger"
            >Password must be at least 8 characters</small>
          </b-form-group>

          <b-form-group
            v-if="edited.id"
            label="Block access"
            label-for="staff-inactive"
          >
            <b-form-checkbox
              id="staff-inactive"
              v-model="edited.inactive"
              switch
              class="mb-1"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <h6 class="mt-2 mb-1">Permissions</h6>
      <p class="text-muted small mb-1">
        Staff member can view listed content by default. Orders are limited to unassigned (non-completed/cancelled) orders and those they supervise, unless the switch below is enabled. Use the table switches to allow creating or changing items.
      </p>
      <div class="table-responsive">
        <table class="table table-sm staff-permissions-table mb-0">
          <thead>
            <tr>
              <th>Content type</th>
              <th
                v-for="ability in permissionAbilityKeys"
                :key="ability"
                class="text-center"
              >
                {{ staffPermissionAbilityLabels[ability] }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="subject in permissionSubjectKeys"
              :key="subject"
            >
              <td>{{ staffPermissionSubjectLabel(subject) }}</td>
              <td
                v-for="ability in permissionAbilityKeys"
                :key="`${subject}-${ability}`"
                class="text-center"
              >
                <b-form-checkbox
                  v-model="edited.permissions[subject][ability]"
                  switch
                  class="d-inline-block"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-form-group
        v-if="companyPurpose !== 'rent'"
        class="mt-2 mb-0"
      >
        <b-form-checkbox
          v-model="edited.permissions.orders.view_others"
          switch
          class="mb-0"
        >
          View orders managed by other supervisors
        </b-form-checkbox>
        <small class="text-muted d-block mt-25 mb-1">
          Allows viewing all company orders, payments, shipments, and returns. Changing still requires Change permission and only applies to unassigned (non-completed/cancelled) orders or orders they supervise.
        </small>
        <b-form-checkbox
          v-model="edited.permissions.orders.change_others"
          switch
          class="mb-0"
          :disabled="!(edited.permissions.orders.view_others && edited.permissions.orders.change) && !edited.permissions.orders.change_others"
          @change="onChangeOthersToggle"
        >
          Change orders managed by other supervisors
        </b-form-checkbox>
        <small class="text-muted d-block mt-25">
          Allows editing any order of the company, including changing the supervisor. Requires “View orders managed by other supervisors” and Orders Change; turning either off clears this permission.
        </small>
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script setup>
import { watch, ref, reactive, nextTick, computed } from 'vue'
import { useStore } from 'vuex'
import {
  BCol,
  BForm,
  BFormCheckbox,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BImg,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import useVuelidate from '@vuelidate/core'
import { email, minLength, required } from '@vuelidate/validators'
import { toastSuccess, toastFailure } from '@/libs/toastification'
import PhoneNumberInput from '@/views/Components/Elements/PhoneNumberInput.vue'
import {
  buildFullPhone,
  ensureDialInOptions,
  isValidFullPhone,
  normalizeDialCode,
  parseFullPhone,
  usePhoneCountries,
} from '@/libs/phoneNumber'
import { backendErrorString, normalizeImageUrl } from '@/libs/utils'
import {
  STAFF_PERMISSION_ABILITIES,
  STAFF_PERMISSION_SUBJECTS,
  createEmptyStaffPermissions,
  staffPermissionSubjectLabel,
} from '@/constants/staffPermissions'

const cloneObject = (object) => JSON.parse(JSON.stringify(object))

const generatePassword = () => {
  const lowercase = 'abcdefghjkmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHJKMNOPQRSTUVWXYZ'
  const numbers = '123456789'
  const symbols = '!@#$%^&*'
  const allChars = lowercase + uppercase + numbers + symbols
  let password = ''

  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += symbols[Math.floor(Math.random() * symbols.length)]

  while (password.length < 8) {
    password += allChars[Math.floor(Math.random() * allChars.length)]
  }

  return password.split('').sort(() => Math.random() - 0.5).join('')
}

const defaultStaff = {
  id: null,
  name: '',
  email: '',
  password: generatePassword(),
  phone: '',
  description: '',
  picture: null,
  picture_preview: null,
  inactive: false,
  permissions: createEmptyStaffPermissions(),
}

const permissionSubjectKeys = Object.keys(STAFF_PERMISSION_SUBJECTS)
const permissionAbilityKeys = Object.keys(STAFF_PERMISSION_ABILITIES)
const staffPermissionAbilityLabels = STAFF_PERMISSION_ABILITIES
const companyPurpose = window.companyPurpose

const props = defineProps({
  staffMember: {
    type: Object,
    default: null,
  },
  clickDate: {
    type: Date,
    default: null,
  },
})

const store = useStore()
const showModal = ref(false)
const isContentLoading = ref(true)
const isFormLoading = ref(false)
const picturePreview = ref('')
const phoneError = ref('')
const emailError = ref('')

const { phoneDialOptions, defaultDialCode, loadPhoneCountries } = usePhoneCountries()
const staffDial = ref(normalizeDialCode(window.companyPhoneCode) || '1')
const staffNumber = ref('')

const edited = reactive(cloneObject(defaultStaff))

const rules = computed(() => ({
  edited: {
    name: { required, minLength: minLength(2) },
    email: { required, email },
    ...(edited.id
      ? {}
      : {
        password: { required, minLength: minLength(8) },
      }),
  },
}))

const v$ = useVuelidate(rules, { edited })

const ensureStaffDialInOptions = () => {
  ensureDialInOptions(staffDial, phoneDialOptions.value, defaultDialCode)
}

const setStaffPhoneFields = (phone) => {
  const parsed = parseFullPhone(phone, phoneDialOptions.value, defaultDialCode)
  staffDial.value = parsed.dialCode
  staffNumber.value = parsed.national
  ensureStaffDialInOptions()
}

const onPhoneInput = () => {
  phoneError.value = ''
}

watch(
  () => edited.email,
  () => {
    emailError.value = ''
  },
)

const onPictureChange = (event) => {
  const file = event?.target?.files?.[0] || null
  edited.picture = file
  if (!file) {
    return
  }

  const reader = new FileReader()
  reader.addEventListener('load', () => {
    picturePreview.value = reader.result
  })
  reader.readAsDataURL(file)
}

const resetStaffForm = () => {
  Object.assign(edited, cloneObject(defaultStaff))
  edited.permissions = createEmptyStaffPermissions()
  edited.password = generatePassword()
  const dialCode = defaultDialCode()
  staffDial.value = dialCode
  staffNumber.value = ''
  phoneError.value = ''
  emailError.value = ''
  picturePreview.value = ''
  ensureStaffDialInOptions()
}

const onChangeOthersToggle = () => {
  if (edited.permissions.orders.change_others) {
    edited.permissions.orders.change = true
    edited.permissions.orders.view_others = true
  }
}

watch(
  () => edited.permissions.orders.change,
  (canChange) => {
    if (!canChange) {
      edited.permissions.orders.change_others = false
    }
  },
)

watch(
  () => edited.permissions.orders.view_others,
  (canViewOthers) => {
    if (!canViewOthers) {
      edited.permissions.orders.change_others = false
    }
  },
)

const validatePhoneForm = () => {
  phoneError.value = ''

  if (!String(staffNumber.value || '').trim()) {
    phoneError.value = 'Phone number is required'
    return false
  }

  if (!isValidFullPhone(buildFullPhone(staffDial.value, staffNumber.value))) {
    phoneError.value = 'Please enter a valid phone number'
    return false
  }

  return true
}

const firstFieldError = (data, field) => {
  if (!data || typeof data !== 'object') {
    return ''
  }
  const nested = data.errors?.[field]
  const direct = data[field]
  const messages = Array.isArray(nested)
    ? nested
    : (Array.isArray(direct) ? direct : [])
  const first = messages.find((message) => typeof message === 'string' && message.trim())
  return first ? first.trim() : ''
}

const applyServerFieldErrors = (error) => {
  const data = error?.response?.data
  phoneError.value = firstFieldError(data, 'phone')
  emailError.value = firstFieldError(data, 'email')
}

const openCreateModal = async () => {
  isContentLoading.value = true
  showModal.value = false
  store.commit('staff/SET_STAFF_MEMBER', null)

  await loadPhoneCountries()
  resetStaffForm()
  v$.value.$reset()

  isContentLoading.value = false
  await nextTick()
  showModal.value = true
}

const openEditModal = async (staffId) => {
  isContentLoading.value = true
  showModal.value = false

  try {
    await loadPhoneCountries()
    const member = await store.dispatch('staff/loadStaffMember', staffId)
    const payload = member?.data ?? member

    edited.id = payload.id
    edited.name = payload.name ?? ''
    edited.email = payload.email ?? ''
    edited.phone = payload.phone ?? ''
    edited.description = payload.description ?? ''
    edited.picture_preview = payload.picture ?? null
    edited.inactive = Number(payload.inactive) === 1
    edited.permissions = createEmptyStaffPermissions()
    if (payload.permissions) {
      permissionSubjectKeys.forEach((subject) => {
        permissionAbilityKeys.forEach((ability) => {
          if (payload.permissions[subject]?.[ability] !== undefined) {
            edited.permissions[subject][ability] = Boolean(payload.permissions[subject][ability])
          }
        })
      })
      if (payload.permissions.orders?.view_others !== undefined) {
        edited.permissions.orders.view_others = Boolean(payload.permissions.orders.view_others)
      }
      if (payload.permissions.orders?.change_others !== undefined) {
        edited.permissions.orders.change_others = Boolean(payload.permissions.orders.change_others)
          && Boolean(payload.permissions.orders?.change)
      }
    }
    picturePreview.value = normalizeImageUrl(payload.picture)
    setStaffPhoneFields(edited.phone)

    isContentLoading.value = false
    await nextTick()
    showModal.value = true
  } catch (error) {
    showModal.value = false
    isContentLoading.value = false
    toastFailure(backendErrorString(error))
  }
}

watch(
  () => props.clickDate,
  async () => {
    if (!props.clickDate) {
      return
    }

    if (props.staffMember !== null && props.staffMember.id) {
      await openEditModal(props.staffMember.id)
      return
    }

    await openCreateModal()
  },
)

const submitForm = async () => {
  isFormLoading.value = true

  const staffPhone = buildFullPhone(staffDial.value, staffNumber.value)
  edited.phone = staffPhone

  const formData = new FormData()
  const appendIfPresent = (key, value) => {
    if (value !== null && value !== undefined && value !== '') {
      formData.append(key, value)
    }
  }

  if (edited.id) {
    formData.append('id', edited.id)
  }
  appendIfPresent('name', edited.name)
  appendIfPresent('email', edited.email)
  appendIfPresent('phone', staffPhone)
  appendIfPresent('description', edited.description)
  if (!edited.id) {
    appendIfPresent('password', edited.password)
  }
  if (edited.id) {
    formData.append('inactive', edited.inactive ? '1' : '0')
  }
  if (edited.picture) {
    formData.append('picture', edited.picture)
  }

  permissionSubjectKeys.forEach((subject) => {
    permissionAbilityKeys.forEach((ability) => {
      formData.append(
        `permissions[${subject}][${ability}]`,
        edited.permissions[subject][ability] ? '1' : '0',
      )
    })
  })
  formData.append(
    'permissions[orders][view_others]',
    edited.permissions.orders?.view_others ? '1' : '0',
  )
  formData.append(
    'permissions[orders][change_others]',
    (
      edited.permissions.orders?.view_others
      && edited.permissions.orders?.change
      && edited.permissions.orders?.change_others
    ) ? '1' : '0',
  )

  try {
    if (edited.id) {
      await store.dispatch('staff/updateStaff', { formData })
      toastSuccess('Staff member updated')
    } else {
      await store.dispatch('staff/addStaff', { formData })
      toastSuccess('Staff member created')
    }
    showModal.value = false
    edited.picture = null
  } catch (error) {
    applyServerFieldErrors(error)
    toastFailure(backendErrorString(error))
  } finally {
    isFormLoading.value = false
  }
}

const onSubmit = async (event) => {
  event.preventDefault()

  const isFormCorrect = await v$.value.$validate()
  const isPhoneValid = validatePhoneForm()

  if (!isFormCorrect || !isPhoneValid) {
    return
  }

  await submitForm()
}
</script>
